import { z } from "zod";
import { CALL_PRICING_MODEL_FLAT_RATE, SUBSCRIPTION_PLAN } from "../constants/common";

const FLAT_RATE_MIN_DURATION = CALL_PRICING_MODEL_FLAT_RATE.MIN_DURATION_MINUTES;
const FLAT_RATE_MAX_DURATION = CALL_PRICING_MODEL_FLAT_RATE.MAX_DURATION_MINUTES;

const callAmountSchema = z
  .number()
  .min(0, "Amount must be at least 0")
  .max(1000, "Amount cannot exceed $1000");

/** Empty or unset call time requirement defaults to 1 minute (no special minimum). */
const normalizeMinCallMinutes = (val) => {
  if (val === '' || val == null) return 1;
  const n = Number(val);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.round(n);
};

const perMinutePricingSchema = z.object({
  amount: callAmountSchema,
  minCallMinutess: z.preprocess(
    normalizeMinCallMinutes,
    z.number().min(1).optional()
  ),
});

const flatRatePricingSchema = z.object({
  amount: callAmountSchema,
  durationMinutes: z
    .number()
    .min(
      FLAT_RATE_MIN_DURATION,
      `Duration must be at least ${FLAT_RATE_MIN_DURATION} minutes`
    )
    .max(
      FLAT_RATE_MAX_DURATION,
      `Duration cannot exceed ${FLAT_RATE_MAX_DURATION} minutes`
    )
    .optional(),
});

// Voice/Video pricing model schema (legacy export shape)
const pricingModelSchema = perMinutePricingSchema.merge(flatRatePricingSchema);

// Voice/Video call settings schema
const callSettingsSchema = z.object({
  enabled: z.boolean().default(true),
  pricingModel: z.enum(['per_minute', 'flat_rate']).default('per_minute'),
  perMinute: perMinutePricingSchema,
  flatRate: flatRatePricingSchema,
  available: z.boolean().default(true),
}).refine(
  (data) => {
    if (data.pricingModel === 'flat_rate') {
      const duration = data.flatRate?.durationMinutes;
      if (
        typeof duration !== 'number' ||
        isNaN(duration) ||
        duration < FLAT_RATE_MIN_DURATION ||
        duration > FLAT_RATE_MAX_DURATION
      ) {
        return false;
      }
    }
    return true;
  },
  {
    message: `Flat rate duration must be between ${FLAT_RATE_MIN_DURATION} and ${FLAT_RATE_MAX_DURATION} minutes`,
    path: ['flatRate', 'durationMinutes'],
  }
);

// Subscription price validation (for CREATOR_SUB only)
const subscriptionPriceSchema = z
  .string()
  .optional()
  .refine(
    (val) => {
      if (!val || val === '' || val === '$') return true; // Allow empty      // Accept values formatted as number with up to 2 decimals
      // Accepts: '10', '10.5', '10.55', rejects: '10.555', 'abc'
      const regex = /^\$?\d+(\.\d{1,2})?$/;
      return regex.test(val.trim());
    },
    { message: "Please enter a number with up to 2 decimals (e.g., 10.99)" }
  )
  .refine(
    (val) => {
      if (!val || val === '' || val === '$') return true; // Allow empty
      const numericPrice = parseFloat(val.replace('$', '').trim());
      return !isNaN(numericPrice);
    },
    { message: "Please enter a valid price" }
  )
  .refine(
    (val) => {
      if (!val || val === '' || val === '$') return true; // Allow empty
      const numericPrice = parseFloat(val.replace('$', '').trim());
      return numericPrice >= SUBSCRIPTION_PLAN.MIN_PRICE;
    },
    { message: `Price must be at least $${SUBSCRIPTION_PLAN.MIN_PRICE}` }
  )
  .refine(
    (val) => {
      if (!val || val === '' || val === '$') return true; // Allow empty
      const numericPrice = parseFloat(val.replace('$', '').trim());
      return numericPrice <= SUBSCRIPTION_PLAN.MAX_PRICE;
    },
    { message: `Price cannot exceed $${SUBSCRIPTION_PLAN.MAX_PRICE}` }
  );

// Promotion campaign schema (for CREATOR_SUB only)
// Aligned with API schema requirements
const promotionSchema = z
  .object({
    applies_to: z.literal('subscription').default('subscription'),
    offer_type: z.enum(['free_trial', 'first_month']).default('first_month'),
    subscriber_type: z.enum(['new', 'expired', 'both']).default('new'),
    offer_expiration_days: z.number().nullable().optional(),
    free_trial_duration_days: z.number().nullable().optional(),
    discount: z
      .object({
        type: z.literal('percent'),
        value: z.number().min(1, 'Discount must be at least 1%').max(100)
      })
      .nullable()
      .optional(),
    limit_type: z.enum(['no_limit', 'fixed']).default('no_limit'),
    offer_limit: z.number().nullable().optional(),
    auto_reset_enabled: z.boolean().default(false),
    per_user_limit: z.number().default(1),
    max_redemptions: z.number().nullable().optional(),
  })
  // If offer_type is first_month, discount is required and must be > 0
  .refine(
    (data) => {
      if (data.offer_type !== 'first_month') return true;
      const hasDiscount = !!data.discount && data.discount.type === 'percent' && typeof data.discount.value === 'number';
      return hasDiscount && data.discount.value > 0 && data.discount.value <= 100;
    },
    {
      message: 'Discount percentage is required for Discount offer and must be between 1 and 100',
      path: ['discount'],
    }
  )
  // If offer_type is first_month, free_trial_duration_days must be null/undefined
  .refine(
    (data) => {
      if (data.offer_type !== 'first_month') return true;
      return data.free_trial_duration_days == null;
    },
    {
      message: 'Free trial duration should not be set for First month discount',
      path: ['free_trial_duration_days'],
    }
  )
  // If offer_type is free_trial, free_trial_duration_days is required and >= 1
  .refine(
    (data) => {
      if (data.offer_type !== 'free_trial') return true;
      return typeof data.free_trial_duration_days === 'number' && data.free_trial_duration_days >= 1;
    },
    {
      message: 'Free trial duration is required and must be at least 1 day',
      path: ['free_trial_duration_days'],
    }
  )
  // If offer_type is free_trial, discount must be null/undefined
  .refine(
    (data) => {
      if (data.offer_type !== 'free_trial') return true;
      return data.discount == null;
    },
    {
      message: 'Discount should not be set for Free trial offers',
      path: ['discount'],
    }
  );

/** Empty string is valid (optional field). Otherwise must be http(s) with host throne.com or throne.me. */
function isValidThroneWishlistUrl(val) {
  if (!val || val === '') return true;
  try {
    const url = new URL(val);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    const host = url.hostname.toLowerCase();
    return (
      host === 'throne.com' ||
      host === 'www.throne.com' ||
      host === 'throne.me' ||
      host === 'www.throne.me'
    );
  } catch {
    return false;
  }
}

// Main form data schema (common fields for all creators)
const formDataSchema = z.object({
  pinMessage: z
    .string()
    .max(150, "Pin message cannot exceed 150 characters")
    .optional()
    .default(''),
  wishlistUrl: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        try {
          const url = new URL(val);
          // Check if it's a valid URL with http or https
          if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return false;
          }
          // Check if it's an Amazon domain
          const hostname = url.hostname.toLowerCase();
          if (!hostname.includes('amazon')) {
            return false;
          }
          return true;
        } catch {
          return false;
        }
      },
      { message: "You can only save Amazon wish list links." }
    )
    .default(''),
  throneWishlistUrl: z
    .string()
    .optional()
    .refine(isValidThroneWishlistUrl, {
      message: 'Throne links must use https://throne.com/... or https://throne.me/...',
    })
    .default(''),
  profile_description: z
    .string()
    .max(1000, "Bio cannot exceed 1000 characters")
    .optional()
    .default(''),
  voice: callSettingsSchema,
  video: callSettingsSchema,
});

// Factory function to create schema based on user role
export const createEditProfileSchema = (showSubscriptionSections = false) => {
  if (showSubscriptionSections) {
    // Schema for CREATOR_SUB (includes subscription and promotion)
    return z.object({
      formData: formDataSchema,
      subscription: z.object({
        subscriptionPrice: subscriptionPriceSchema,
      }),
      promotion: promotionSchema,
    });
  } else {
    // Schema for CREATOR_CONTENT (only formData)
    return z.object({
      formData: formDataSchema,
    });
  }
};

// Export individual schemas for reuse
export {
  formDataSchema,
  subscriptionPriceSchema,
  promotionSchema,
  callSettingsSchema,
  pricingModelSchema,
  normalizeMinCallMinutes,
};

